import { useEffect, useRef, useState } from "react";
import StudentLayout from "@/components/StudentLayout";
import api, { apiErr, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Clock, ArrowLeft, Send, Loader2, Camera, X, CheckCircle2, AlertTriangle, ChevronLeft, ChevronRight, PenTool, Trophy, XCircle } from "lucide-react";
import { MathInline } from "@/components/MathText";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const timeLeft = (iso) => {
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "closed";
  const h = Math.floor(ms / 3.6e6), d = Math.floor(h / 24);
  if (d >= 1) return `${d} day${d > 1 ? "s" : ""} left`;
  if (h >= 1) return `${h} hour${h > 1 ? "s" : ""} left`;
  return `${Math.max(1, Math.floor(ms / 6e4))} min left`;
};

const STATUS_STYLE = {
  pending: "bg-sky-50 text-sky-600",
  submitted: "bg-amber-50 text-amber-600",
  graded: "bg-emerald-50 text-emerald-600",
  missed: "bg-rose-50 text-rose-600",
};

export default function StudentExams() {
  const [taking, setTaking] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [practice, setPractice] = useState(null); // subject name
  if (practice) return <BlanksPractice subject={practice} onBack={() => setPractice(null)} />;
  if (taking) return <TakeExam examId={taking} onBack={() => setTaking(null)} />;
  if (reviewing) return <ReviewExam examId={reviewing} onBack={() => setReviewing(null)} />;
  return <ExamsList onTake={setTaking} onReview={setReviewing} onPractice={setPractice} />;
}

/* ─────────────────── Fill-in-the-Blanks practice (auto-graded) ─────────────────── */
function BlanksSection({ onPractice }) {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/student/blanks/subjects"); setSubjects(data.subjects || []); }
      catch { /* silent — feature optional */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || subjects.length === 0) return null;

  return (
    <section className="mb-8" data-testid="blanks-practice-section">
      <div className="flex items-center gap-2 mb-3 px-1">
        <PenTool className="h-4 w-4 text-emerald-500" />
        <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">Fill-in-the-Blanks practice</h2>
        <span className="text-[11px] text-slate-400">auto-graded · unlimited attempts</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {subjects.map((s) => (
          <button
            key={s.subject}
            onClick={() => onPractice(s.subject)}
            className="text-left rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 hover:border-emerald-400 hover:shadow-sm transition group"
            data-testid={`blanks-subject-${s.subject}`}
          >
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900 dark:text-slate-100">{s.subject}</div>
              {s.best_pct != null && (
                <span className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300">
                  <Trophy className="h-3 w-3" /> {s.best_pct}%
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-1">{s.count} question{s.count !== 1 ? "s" : ""}</div>
            <div className="text-xs text-emerald-600 font-medium mt-2 opacity-0 group-hover:opacity-100 transition">Start practice →</div>
          </button>
        ))}
      </div>
    </section>
  );
}

function BlanksPractice({ subject, onBack }) {
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({}); // qid -> [idx per blank]
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setResult(null);
    setAnswers({});
    try {
      const { data } = await api.get("/student/blanks/practice", { params: { subject } });
      setQuestions(data.questions || []);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not load practice"); onBack(); }
  };
  useEffect(() => { load(); }, [subject]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (qid, bi, oi) =>
    setAnswers((a) => {
      const cur = [...(a[qid] || [])];
      cur[bi] = oi;
      return { ...a, [qid]: cur };
    });

  const totalBlanks = (questions || []).reduce((n, q) => n + q.blank_count, 0);
  const answeredBlanks = Object.values(answers).reduce((n, arr) => n + arr.filter((x) => x != null && x >= 0).length, 0);

  const submit = async () => {
    if (answeredBlanks < totalBlanks) { if (!window.confirm("Some blanks are unanswered. Submit anyway?")) return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/student/blanks/submit", { answers }, { params: { subject } });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not submit"); }
    finally { setSubmitting(false); }
  };

  const letters = ["A", "B", "C", "D"];
  const renderSentence = (s) => s.replace(/_{2,}/g, "＿＿＿");

  if (!questions) return <StudentLayout><div className="max-w-3xl mx-auto text-slate-500">Loading…</div></StudentLayout>;

  return (
    <StudentLayout>
      <div className="max-w-3xl mx-auto pb-28" data-testid="blanks-practice">
        <Button variant="ghost" size="sm" className="rounded-full mb-4" onClick={onBack} data-testid="blanks-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>

        {/* Result banner */}
        {result && (
          <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 p-5 mb-5 text-center" data-testid="blanks-result">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-300 mb-1">Your score</div>
            <div className="text-4xl font-bold text-emerald-700 dark:text-emerald-200" style={{ fontFamily: "Outfit" }} data-testid="blanks-score">{result.score_pct}%</div>
            <div className="text-sm text-emerald-700/80 dark:text-emerald-300/80 mt-1">{result.correct_blanks} of {result.total_blanks} blanks correct</div>
            <Button size="sm" className="rounded-full mt-3 bg-emerald-600 hover:bg-emerald-700" onClick={load} data-testid="blanks-retry"><PenTool className="h-3.5 w-3.5 mr-1.5" /> Try again</Button>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
          <div className="text-center border-b border-slate-200 dark:border-slate-700 p-5">
            <div className="text-lg font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}><PenTool className="inline h-5 w-5 text-emerald-500 mr-2 -mt-1" /> {subject} — Fill in the Blanks</div>
            <div className="text-xs text-slate-400 mt-1">{questions.length} question{questions.length !== 1 ? "s" : ""} · pick the correct option for each blank</div>
          </div>

          <div className="p-5 space-y-6" data-testid="blanks-questions">
            {questions.map((q, i) => {
              const res = result?.results?.find((r) => r.id === q.id);
              return (
                <div key={q.id} data-testid={`blanks-q-${q.id}`}>
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{i + 1}. {renderSentence(q.sentence)}</div>
                  <div className="mt-3 space-y-3">
                    {q.blanks.map((b, bi) => {
                      const resBlank = res?.blanks?.[bi];
                      return (
                        <div key={bi}>
                          {q.blank_count > 1 && <div className="text-xs text-slate-400 mb-1">Blank {bi + 1}</div>}
                          <div className="grid sm:grid-cols-2 gap-2">
                            {b.options.map((opt, oi) => {
                              const chosen = answers[q.id]?.[bi] === oi;
                              let tone = "border-slate-200 dark:border-slate-700 hover:border-emerald-300";
                              if (result && resBlank) {
                                if (oi === resBlank.correct_index) tone = "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200";
                                else if (oi === resBlank.picked_index) tone = "border-rose-400 bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-200";
                                else tone = "border-slate-200 dark:border-slate-700 opacity-60";
                              } else if (chosen) tone = "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30";
                              return (
                                <button
                                  key={oi}
                                  type="button"
                                  disabled={!!result}
                                  onClick={() => pick(q.id, bi, oi)}
                                  className={`text-left text-sm rounded-xl border px-3 py-2 flex items-center gap-2 transition ${tone}`}
                                  data-testid={`blanks-opt-${q.id}-${bi}-${oi}`}
                                >
                                  <span className="h-5 w-5 shrink-0 rounded-full border border-current/40 flex items-center justify-center text-[11px] font-bold">{letters[oi]}</span>
                                  <span className="flex-1">{opt}</span>
                                  {result && resBlank && oi === resBlank.correct_index && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                                  {result && resBlank && oi === resBlank.picked_index && oi !== resBlank.correct_index && <XCircle className="h-4 w-4 text-rose-500" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {!result && (
          <div className="fixed bottom-0 left-0 right-0 sm:static bg-white/90 dark:bg-slate-900/90 backdrop-blur sm:bg-transparent border-t sm:border-0 border-slate-200 dark:border-slate-700 p-4 sm:p-0 sm:mt-5">
            <div className="max-w-3xl mx-auto flex items-center justify-between">
              <span className="text-xs text-slate-400">{answeredBlanks}/{totalBlanks} answered</span>
              <Button onClick={submit} disabled={submitting} className="rounded-full h-11 px-8 bg-emerald-500 hover:bg-emerald-600" data-testid="blanks-submit">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Submit &amp; see score
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
}

function ExamsList({ onTake, onReview, onPractice }) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/student/exams"); setExams(data.exams || []); }
      catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not load tests"); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <StudentLayout>
      <div className="max-w-3xl mx-auto" data-testid="student-exams-page">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">My tests</div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-6" style={{ fontFamily: "Outfit" }}>
          <FileText className="inline h-7 w-7 text-indigo-500 mr-2 -mt-1" /> Assigned Tests
        </h1>

        <BlanksSection onPractice={onPractice} />

        {loading ? <div className="text-slate-500">Loading…</div>
          : exams.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-12 text-center" data-testid="student-exams-empty">
              <FileText className="h-8 w-8 mx-auto text-slate-300 mb-3" />
              <div className="font-medium text-slate-900 dark:text-slate-100">No tests assigned</div>
              <div className="text-slate-500 text-sm mt-1">Your teacher's tests will appear here.</div>
            </div>
          ) : (
            <div className="space-y-6" data-testid="student-exams-list">
              {Object.entries(exams.reduce((acc, e) => {
                const key = e.subject || "Other";
                (acc[key] = acc[key] || []).push(e);
                return acc;
              }, {})).sort(([a], [b]) => a.localeCompare(b)).map(([subject, list]) => {
                const pending = list.filter((e) => !e.submitted && !e.locked).length;
                return (
                  <section key={subject} data-testid={`student-exam-subject-${subject}`}>
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">{subject}</h2>
                      <span className="text-[11px] text-slate-400">{list.length} test{list.length > 1 ? "s" : ""}</span>
                      {pending > 0 && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300" data-testid={`student-exam-subject-pending-${subject}`}>
                          {pending} to do
                        </span>
                      )}
                    </div>
                    <div className="space-y-3">
                      {list.map((e) => {
                        const canTake = !e.submitted && !e.locked;
                        return (
                          <div key={e.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5" data-testid={`student-exam-${e.id}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="font-semibold text-slate-900 dark:text-slate-100">{e.title}</div>
                                <div className="text-xs text-slate-500 mt-0.5">by {e.teacher_name} · {e.num_questions} questions · {e.total_marks} marks</div>
                              </div>
                              <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_STYLE[e.status] || "bg-slate-100 text-slate-500"}`} data-testid={`student-exam-status-${e.id}`}>{e.status}</span>
                            </div>
                            <div className="flex items-center justify-between mt-3">
                              <div className="text-xs text-slate-400 inline-flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" /> Due {fmtDate(e.due_at)} {!e.locked && !e.submitted && <span className="text-indigo-500 font-medium ml-1">· {timeLeft(e.due_at)}</span>}
                              </div>
                              <div className="flex items-center gap-2">
                                {e.status === "graded" && <span className="text-sm font-semibold text-emerald-600">{e.score}/{e.total_marks}</span>}
                                {e.submitted && e.status !== "graded" && <span className="text-xs text-amber-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Submitted</span>}
                                {e.status === "missed" && <span className="text-xs text-rose-500 inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Missed</span>}
                                {canTake && <Button size="sm" className="rounded-full bg-indigo-500 hover:bg-indigo-600" onClick={() => onTake(e.id)} data-testid={`student-take-${e.id}`}>Start test</Button>}
                                {e.submitted && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onReview(e.id)} data-testid={`student-review-${e.id}`}>View</Button>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
      </div>
    </StudentLayout>
  );
}

function TakeExam({ examId, onBack }) {
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({}); // qid -> {selected, text}
  const [images, setImages] = useState([]); // data URLs
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/student/exams/${examId}`);
        if (data.submitted) { toast.info("You have already submitted this test."); onBack(); return; }
        if (data.locked) { toast.error("This test is closed."); onBack(); return; }
        setExam(data);
      } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not load test"); onBack(); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  const setSel = (qid, idx) => setAnswers((a) => ({ ...a, [qid]: { ...(a[qid] || {}), selected: idx } }));
  const setText = (qid, t) => setAnswers((a) => ({ ...a, [qid]: { ...(a[qid] || {}), text: t } }));
  const setBlank = (qid, bi, oi) => setAnswers((a) => {
    const cur = [...((a[qid] || {}).selected_blanks || [])];
    cur[bi] = oi;
    return { ...a, [qid]: { ...(a[qid] || {}), selected_blanks: cur } };
  });
  const setMatch = (qid, li, oi) => setAnswers((a) => {
    const cur = [...((a[qid] || {}).selected_matches || [])];
    cur[li] = oi;
    return { ...a, [qid]: { ...(a[qid] || {}), selected_matches: cur } };
  });

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    files.forEach((f) => {
      if (!/^image\//.test(f.type)) { toast.error("Only image files."); return; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is too large (max 10 MB).`); return; }
      const reader = new FileReader();
      reader.onload = () => setImages((prev) => prev.length >= 6 ? prev : [...prev, reader.result]);
      reader.readAsDataURL(f);
    });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        answers: exam.questions.map((q) => ({
          question_id: q.id,
          selected: answers[q.id]?.selected ?? -1,
          text: answers[q.id]?.text || "",
          selected_blanks: answers[q.id]?.selected_blanks || [],
          selected_matches: answers[q.id]?.selected_matches || [],
        })),
        images,
      };
      const { data } = await api.post(`/student/exams/${examId}/submit`, payload);
      toast.success(data.needs_grading ? "Submitted! Your teacher will grade it soon." : `Submitted! Score: ${data.auto_score}/${data.max_score}`);
      onBack();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not submit");
    } finally { setSubmitting(false); }
  };

  if (!exam) return <StudentLayout><div className="max-w-3xl mx-auto text-slate-500">Loading…</div></StudentLayout>;

  return (
    <StudentLayout>
      <div className="max-w-3xl mx-auto pb-24" data-testid="take-exam">
        <Button variant="ghost" size="sm" className="rounded-full mb-4" onClick={onBack} data-testid="take-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
          <div className="text-center border-b border-slate-200 dark:border-slate-700 p-5">
            <div className="text-xl font-bold tracking-wide text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>TRISHUL HIGH SCHOOL</div>
            <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">{exam.title} · {exam.subject}</div>
            <div className="text-xs text-slate-400 mt-0.5">Total marks: {exam.total_marks} · Due {fmtDate(exam.due_at)}</div>
            {exam.instructions && <div className="text-xs text-slate-500 mt-2 italic">{exam.instructions}</div>}
          </div>

          <div className="p-5 space-y-5" data-testid="take-questions">
            {exam.questions.map((q, i) => (
              <div key={q.id} data-testid={`take-q-${q.id}`}>
                <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  {i + 1}. <MathInline text={q.prompt} /> <span className="text-xs text-slate-400">({q.marks} marks)</span>
                </div>
                {q.figure_svg && (
                  <div className="mt-2 inline-block rounded-lg border border-slate-200 dark:border-slate-700 bg-white p-2 [&_svg]:max-w-[280px] [&_svg]:h-auto" dangerouslySetInnerHTML={{ __html: q.figure_svg }} />
                )}
                {q.figure_image && (
                  <img src={`${API_BASE}${q.figure_image}`} alt="figure" className="mt-2 max-w-[300px] max-h-[240px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white" data-testid={`take-q-img-${q.id}`} />
                )}
                {q.type === "mcq" ? (
                  <div className="mt-2 space-y-1.5 pl-4">
                    {q.options.map((opt, oi) => (
                      <label key={oi} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer">
                        <input type="radio" name={`q-${q.id}`} checked={answers[q.id]?.selected === oi} onChange={() => setSel(q.id, oi)} data-testid={`take-${q.id}-opt-${oi}`} />
                        <MathInline text={opt} />
                      </label>
                    ))}
                  </div>
                ) : q.type === "blanks" ? (
                  <div className="mt-2 pl-4 space-y-3" data-testid={`take-blanks-${q.id}`}>
                    {(q.blanks || []).map((b, bi) => (
                      <div key={bi}>
                        {(q.blanks || []).length > 1 && <div className="text-xs text-slate-400 mb-1">Blank {bi + 1}</div>}
                        <div className="grid sm:grid-cols-2 gap-2">
                          {(b.options || []).map((opt, oi) => {
                            const chosen = answers[q.id]?.selected_blanks?.[bi] === oi;
                            return (
                              <button key={oi} type="button" onClick={() => setBlank(q.id, bi, oi)}
                                className={`text-left text-sm rounded-xl border px-3 py-2 flex items-center gap-2 transition ${chosen ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30" : "border-slate-200 dark:border-slate-700 hover:border-indigo-300"}`}
                                data-testid={`take-blank-${q.id}-${bi}-${oi}`}>
                                <span className="h-5 w-5 shrink-0 rounded-full border border-current/40 flex items-center justify-center text-[11px] font-bold">{String.fromCharCode(65 + oi)}</span>
                                <span className="flex-1">{opt}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : q.type === "match" ? (
                  <div className="mt-2 pl-4 space-y-2" data-testid={`take-match-${q.id}`}>
                    {(q.lefts || []).map((lft, li) => (
                      <div key={li} className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-slate-700 dark:text-slate-200 min-w-[8rem]">{li + 1}. {lft}</span>
                        <span className="text-slate-400">→</span>
                        <select
                          value={answers[q.id]?.selected_matches?.[li] ?? ""}
                          onChange={(e) => setMatch(q.id, li, e.target.value === "" ? -1 : parseInt(e.target.value, 10))}
                          className="h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-2 flex-1 min-w-[10rem]"
                          data-testid={`take-match-${q.id}-${li}`}
                        >
                          <option value="">— choose —</option>
                          {(q.right_options || []).map((r, oi) => <option key={oi} value={oi}>{r}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                ) : q.type === "poem" ? (
                  <div className="mt-2 pl-4" data-testid={`take-poem-${q.id}`}>
                    <div className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed bg-slate-50 dark:bg-slate-900/40 rounded-xl p-3">{(q.poem || "").replace(/_{2,}/g, "＿＿＿＿")}</div>
                    {(q.alt_poem || "").trim() && (
                      <>
                        <div className="text-xs font-semibold text-center text-slate-500 my-2">ಅಥವಾ / OR</div>
                        <div className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed bg-slate-50 dark:bg-slate-900/40 rounded-xl p-3">{(q.alt_poem || "").replace(/_{2,}/g, "＿＿＿＿")}</div>
                      </>
                    )}
                    <Textarea
                      value={answers[q.id]?.text || ""}
                      onChange={(e) => setText(q.id, e.target.value)}
                      placeholder="Write the completed poem here (or answer on paper and upload a photo below)…"
                      className="rounded-xl mt-2 min-h-[100px]"
                      data-testid={`take-${q.id}-poem-text`}
                    />
                  </div>
                ) : (
                  <Textarea
                    value={answers[q.id]?.text || ""}
                    onChange={(e) => setText(q.id, e.target.value)}
                    placeholder={q.type === "long" ? "Write your answer…" : "Short answer…"}
                    className={`rounded-xl mt-2 ${q.type === "long" ? "min-h-[120px]" : "min-h-[60px]"}`}
                    data-testid={`take-${q.id}-text`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Photo upload of handwritten answers */}
          <div className="p-5 border-t border-slate-200 dark:border-slate-700">
            <div className="text-sm font-medium text-slate-800 dark:text-slate-100">Or upload a photo of your handwritten answers</div>
            <div className="text-xs text-slate-500 mb-3">You can write on paper, take a photo and upload it (up to 6 images, 10 MB each).</div>
            <div className="flex flex-wrap gap-3">
              {images.map((src, idx) => (
                <div key={idx} className="relative">
                  <img src={src} alt="answer" className="h-24 w-auto rounded-lg border border-slate-200 dark:border-slate-700" />
                  <button onClick={() => setImages((p) => p.filter((_, i) => i !== idx))} className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-0.5" data-testid={`take-img-remove-${idx}`}><X className="h-3 w-3" /></button>
                </div>
              ))}
              {images.length < 6 && (
                <button onClick={() => fileRef.current?.click()} className="h-24 w-24 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 flex flex-col items-center justify-center text-slate-400 hover:border-indigo-400 hover:text-indigo-500" data-testid="take-upload-btn">
                  <Camera className="h-5 w-5 mb-1" /><span className="text-[10px]">Add photo</span>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={onFiles} data-testid="take-file-input" />
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 sm:static bg-white/90 dark:bg-slate-900/90 backdrop-blur sm:bg-transparent border-t sm:border-0 border-slate-200 dark:border-slate-700 p-4 sm:p-0 sm:mt-5">
          <div className="max-w-3xl mx-auto flex justify-end">
            <Button onClick={submit} disabled={submitting} className="rounded-full h-11 px-8 bg-indigo-500 hover:bg-indigo-600" data-testid="take-submit">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Submit test
            </Button>
          </div>
        </div>
      </div>
    </StudentLayout>
  );
}

function ReviewExam({ examId, onBack }) {
  const [rev, setRev] = useState(null);
  const [imgUrls, setImgUrls] = useState([]);
  const [viewerIdx, setViewerIdx] = useState(null); // index of image shown in the modal

  useEffect(() => {
    let urls = [];
    (async () => {
      try {
        const { data } = await api.get(`/student/exams/${examId}/review`);
        setRev(data);
        // fetch the student's own uploaded answer photos as blobs
        urls = await Promise.all((data.image_ids || []).map(async (fid) => {
          try {
            const res = await api.get(`/student/exams/file/${fid}`, { responseType: "blob" });
            return URL.createObjectURL(res.data);
          } catch { return null; }
        }));
        setImgUrls(urls.filter(Boolean));
      } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not load your test"); onBack(); }
    })();
    return () => { urls.forEach((u) => u && URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  if (!rev) return <StudentLayout><div className="max-w-3xl mx-auto text-slate-500">Loading…</div></StudentLayout>;

  const graded = rev.status === "graded";

  return (
    <StudentLayout>
      <div className="max-w-3xl mx-auto pb-16" data-testid="review-exam">
        <Button variant="ghost" size="sm" className="rounded-full mb-4" onClick={onBack} data-testid="review-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
          <div className="text-center border-b border-slate-200 dark:border-slate-700 p-5">
            <div className="text-xl font-bold tracking-wide text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>TRISHUL HIGH SCHOOL</div>
            <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">{rev.title} · {rev.subject}</div>
            <div className="mt-2 flex items-center justify-center gap-2">
              <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${graded ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`} data-testid="review-status">{rev.status}</span>
              {graded && <span className="text-sm font-semibold text-emerald-600" data-testid="review-score">{rev.score}/{rev.max_score}</span>}
            </div>
            {!rev.reveal && (
              <div className="text-xs text-slate-500 mt-2 italic">Correct answers will be shown once the test closes or your teacher grades it.</div>
            )}
          </div>

          <div className="p-5 space-y-5" data-testid="review-questions">
            {rev.questions.map((q, i) => {
              const isMcq = q.type === "mcq";
              const isBlanks = q.type === "blanks";
              return (
                <div key={q.id} className="rounded-xl border border-slate-100 dark:border-slate-700/60 p-4" data-testid={`review-q-${q.id}`}>
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                    {i + 1}. <MathInline text={q.prompt} /> <span className="text-xs text-slate-400">({q.marks} marks)</span>
                    {q.awarded != null && <span className="ml-2 text-xs font-semibold text-emerald-600">· {q.awarded}/{q.marks}</span>}
                  </div>
                  {q.figure_svg && (
                    <div className="mt-2 inline-block rounded-lg border border-slate-200 dark:border-slate-700 bg-white p-2 [&_svg]:max-w-[280px] [&_svg]:h-auto" dangerouslySetInnerHTML={{ __html: q.figure_svg }} />
                  )}
                  {q.figure_image && (
                    <img src={`${API_BASE}${q.figure_image}`} alt="figure" className="mt-2 max-w-[300px] max-h-[240px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white" data-testid={`review-q-img-${q.id}`} />
                  )}
                  {isMcq ? (
                    <div className="mt-2 space-y-1.5 pl-1">
                      {q.options.map((opt, oi) => {
                        const chosen = q.your_selected === oi;
                        const isCorrectOpt = rev.reveal && q.correct_index === oi;
                        const tone = isCorrectOpt ? "text-emerald-700 dark:text-emerald-300 font-medium"
                          : (chosen && rev.reveal && !isCorrectOpt) ? "text-rose-600 dark:text-rose-400"
                          : chosen ? "text-slate-800 dark:text-slate-100 font-medium" : "text-slate-500 dark:text-slate-400";
                        return (
                          <div key={oi} className={`text-sm flex items-center gap-2 ${tone}`} data-testid={`review-${q.id}-opt-${oi}`}>
                            <span className="w-4">{String.fromCharCode(65 + oi)}.</span>
                            <MathInline text={opt} />
                            {chosen && <span className="text-[11px] text-slate-400 ml-1">(your answer)</span>}
                            {isCorrectOpt && <span className="text-[11px] text-emerald-600 ml-1">✓ correct</span>}
                          </div>
                        );
                      })}
                      {q.your_selected === -1 && <div className="text-xs text-rose-500">You did not answer this question.</div>}
                    </div>
                  ) : isBlanks ? (
                    <div className="mt-2 pl-1 space-y-3" data-testid={`review-blanks-${q.id}`}>
                      {(q.blanks || []).map((b, bi) => {
                        const picked = (q.your_selected_blanks || [])[bi];
                        return (
                          <div key={bi}>
                            {(q.blanks || []).length > 1 && <div className="text-xs text-slate-400 mb-1">Blank {bi + 1}</div>}
                            <div className="space-y-1">
                              {(b.options || []).map((opt, oi) => {
                                const chosen = picked === oi;
                                const isCorrectOpt = rev.reveal && b.correct_index === oi;
                                const tone = isCorrectOpt ? "text-emerald-700 dark:text-emerald-300 font-medium"
                                  : (chosen && rev.reveal && !isCorrectOpt) ? "text-rose-600 dark:text-rose-400"
                                  : chosen ? "text-slate-800 dark:text-slate-100 font-medium" : "text-slate-500 dark:text-slate-400";
                                return (
                                  <div key={oi} className={`text-sm flex items-center gap-2 ${tone}`} data-testid={`review-blank-${q.id}-${bi}-${oi}`}>
                                    <span className="w-4">{String.fromCharCode(65 + oi)}.</span>
                                    <span>{opt}</span>
                                    {chosen && <span className="text-[11px] text-slate-400 ml-1">(your answer)</span>}
                                    {isCorrectOpt && <span className="text-[11px] text-emerald-600 ml-1">✓ correct</span>}
                                  </div>
                                );
                              })}
                              {picked == null && <div className="text-xs text-rose-500">Not answered.</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : q.type === "match" ? (
                    <div className="mt-2 pl-1 space-y-1" data-testid={`review-match-${q.id}`}>
                      {(q.lefts || []).map((lft, li) => {
                        const ropts = q.right_options || [];
                        const idx = (q.your_selected_matches || [])[li];
                        const chose = (idx != null && idx >= 0 && idx < ropts.length) ? ropts[idx] : null;
                        const correct = rev.reveal ? (q.correct_rights || [])[li] : null;
                        const ok = correct != null && chose === correct;
                        return (
                          <div key={li} className="text-sm flex flex-wrap items-center gap-1.5" data-testid={`review-match-${q.id}-${li}`}>
                            <span className="text-slate-500">{li + 1}. {lft} →</span>
                            <span className={rev.reveal ? (ok ? "text-emerald-700 dark:text-emerald-300 font-medium" : "text-rose-600 dark:text-rose-400") : "text-slate-800 dark:text-slate-100"}>{chose || "—"}</span>
                            {rev.reveal && !ok && <span className="text-[11px] text-emerald-600 ml-1">✓ {correct}</span>}
                          </div>
                        );
                      })}
                    </div>
                  ) : q.type === "poem" ? (
                    <div className="mt-2" data-testid={`review-poem-${q.id}`}>
                      <div className="text-sm text-slate-500 whitespace-pre-wrap rounded-lg bg-slate-50 dark:bg-slate-900 p-3">{(q.poem || "").replace(/_{2,}/g, "＿＿＿")}</div>
                      <div className="text-xs uppercase tracking-wide text-slate-400 mt-2 mb-1">Your answer</div>
                      <div className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap rounded-lg bg-slate-50 dark:bg-slate-900 p-3 min-h-[40px]">{q.your_text ? q.your_text : <span className="text-slate-400 italic">(written on paper — see photo below if any)</span>}</div>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Your answer</div>
                      <div className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap rounded-lg bg-slate-50 dark:bg-slate-900 p-3 min-h-[40px]" data-testid={`review-${q.id}-your-text`}>
                        {q.your_text ? q.your_text : <span className="text-slate-400 italic">(no written answer — see uploaded photo below if any)</span>}
                      </div>
                      {rev.reveal && q.answer_text && (
                        <div className="mt-2 text-sm rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 p-3">
                          <span className="font-semibold">Model answer: </span><MathInline text={q.answer_text} />
                        </div>
                      )}
                    </div>
                  )}
                  {q.feedback && (
                    <div className="mt-2 text-xs text-slate-600 dark:text-slate-300"><span className="font-semibold">Teacher's note: </span>{q.feedback}</div>
                  )}
                </div>
              );
            })}
          </div>

          {imgUrls.length > 0 && (
            <div className="p-5 border-t border-slate-200 dark:border-slate-700">
              <div className="text-sm font-medium text-slate-800 dark:text-slate-100 mb-3">Your uploaded answer sheets</div>
              <div className="flex flex-wrap gap-3">
                {imgUrls.map((src, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setViewerIdx(idx)}
                    className="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 hover:ring-2 hover:ring-indigo-400 transition"
                    data-testid={`review-img-${idx}`}
                  >
                    <img src={src} alt="answer sheet" className="h-32 w-auto object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={viewerIdx !== null} onOpenChange={(v) => !v && setViewerIdx(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-0" data-testid="answer-sheet-modal">
          {viewerIdx !== null && (
            <div className="relative flex items-center justify-center min-h-[50vh] max-h-[85vh]">
              <img src={imgUrls[viewerIdx]} alt="answer sheet" className="max-h-[85vh] w-auto object-contain" data-testid="answer-sheet-modal-img" />
              {imgUrls.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setViewerIdx((i) => (i - 1 + imgUrls.length) % imgUrls.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center"
                    data-testid="answer-sheet-prev"
                  ><ChevronLeft className="h-6 w-6" /></button>
                  <button
                    type="button"
                    onClick={() => setViewerIdx((i) => (i + 1) % imgUrls.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center"
                    data-testid="answer-sheet-next"
                  ><ChevronRight className="h-6 w-6" /></button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs text-white/80 bg-black/40 rounded-full px-3 py-1">{viewerIdx + 1} / {imgUrls.length}</div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </StudentLayout>
  );
}
